import type { ReactNode } from 'react';
import { TranslateFunction } from 'react-localize-redux';

import { type SettingsMenuItem } from '@fd/ui/List/SettingsList';

import { ItemMetaData } from '../../sidebar/menuItemsConfig';

/** One searchable destination, as the legacy search bar and the command menu both consume it. */
export interface MenuItemOption {
  title: string;
  path: string;
  icon?: ReactNode;
  permissions?: string[];
  viewId?: SettingsMenuItem['viewId'];
  exemptFromViewConfiguration?: boolean;
}

/** Fuse.js options shared by the legacy search bar and the command menu, so both rank results the same way. */
export const fuseOptions = {
  keys: [
    'title',
    'path',
    'searchAlias',
    'propertyName',
    'propertyId',
    'salesChannelName',
    'salesChannelId',
    'salesChannelType',
    'searchByTitlePropertyAndId',
    'searchByPropertyAndIdAndTitle',
    'searchByIdAndTitleAndProperty',
    'searchByChannelNameAndIdAndTitle',
    'searchByIdAndChannelNameAndTitle',
    'searchByChannelNameAndTitleAndId',
    'searchByTitleAndChannelNameAndType',
  ],
  threshold: 0.3,
};

/** The property and sales-channel fields an item may carry, each as a string so Fuse can index it. */
export const getPropertyAndSalesChannelInfo = (item: MenuItemOption | SettingsMenuItem) => {
  // joys of typescript
  let propertyName = '';
  if ('propertyName' in item && item.propertyName) {
    propertyName = item.propertyName as string;
  }
  let propertyId = '';
  if ('propertyId' in item && item.propertyId) {
    propertyId = item.propertyId as string;
  }
  let salesChannelName = '';
  if ('salesChannelName' in item && item.salesChannelName) {
    salesChannelName = item.salesChannelName as string;
  }
  let salesChannelId = '';
  if ('salesChannelId' in item && item.salesChannelId) {
    salesChannelId = item.salesChannelId as string;
  }
  let salesChannelType = '';
  if ('salesChannelType' in item && item.salesChannelType) {
    salesChannelType = item.salesChannelType as string;
  }
  return {
    propertyName,
    propertyId,
    salesChannelName,
    salesChannelId,
    salesChannelType,
  };
};

const extractValidItems = (subItems: ItemMetaData['subItems'], translate: TranslateFunction) =>
  subItems
    ?.filter((sub) => sub.path && !sub.hidden)
    .map((sub) => ({
      title: translate(sub.title) as string,
      // Untranslated key, so a consumer can map the item to its sidebar icon.
      titleKey: sub.title,
      path: sub.path,
      icon: sub.icon,
      searchAlias: sub.searchAlias,
      permissions: sub.permissions,
      viewId: sub.viewId,
      exemptFromViewConfiguration: sub.exemptFromViewConfiguration,
    })) ?? [];

export type flattenedMenuItem = ReturnType<typeof flattenMenuItems>;
export type PropertyItem = Omit<ItemMetaData, 'title'> & {
  title: string;
  noTranslate: boolean;
};
export const flattenMenuItems = (
  items: (ItemMetaData | SettingsMenuItem | PropertyItem)[],
  translate: TranslateFunction
) => {
  return items.flatMap((item) => {
    const topLevel =
      item?.path && !item.subItems && !item.hidden
        ? [
            {
              title:
                'noTranslate' in item && item.noTranslate
                  ? item.title
                  : (translate(item.title as TranslationId) as string),
              titleKey:
                'noTranslate' in item && item.noTranslate
                  ? undefined
                  : (item.title as TranslationId),
              path: item.path,
              icon: item.icon,
              searchAlias: item.searchAlias ?? [],
              propertyId: 'propertyId' in item ? item.propertyId : '',
              propertyName: 'propertyName' in item ? item.propertyName : '',
              permissions: item.permissions,
              viewId: 'viewId' in item ? item.viewId : undefined,
              exemptFromViewConfiguration:
                'exemptFromViewConfiguration' in item
                  ? item.exemptFromViewConfiguration
                  : undefined,
              salesChannelType: 'salesChannelType' in item ? item.salesChannelType : '',
              salesChannelName: 'salesChannelName' in item ? item.salesChannelName : '',
              salesChannelId: 'salesChannelId' in item ? item.salesChannelId : '',
            },
          ]
        : [];

    return [...topLevel, ...extractValidItems(item?.subItems, translate)];
  });
};
